import React, { useState } from 'react';
import {
  Alert, Box, Button, CardContent, CardHeader, Typography,
} from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import Add from '@mui/icons-material/Add';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CircleRoundedIcon from '@mui/icons-material/CircleRounded';
import LocalIQSpinner from '../shared/LoadingSpinner/LoadingSpinner';
import {
  BodyLarge,
  FlexColumn,
  FlexRowWithFlexWrap,
  IconWrapper,
  RemoveButton,
  StyledCard,
  StyledAlert,
  Tile,
} from './styled';
import useMfaManagement from './useMfaManagement';
import DeleteMfaModal from './DeleteMfaModal';
import PasswordConfirmationModal from './PasswordConfirmationModal';
import { determineDashRoot } from './utils';

export default function ProfilePage() {
  const {
    isAuthenticated,
    isLoading,
  } = useAuth0();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [mfaToDelete, setMfaToDelete] = useState(null);

  const {
    isMfaLoading,
    isEnrolling,
    processedMfaList,
    handleAddMfa,
    handleDeleteMfa,
    deleteError,
    clearDeleteError,
  } = useMfaManagement();

  const handleOpenDeleteModal = (mfa) => {
    setMfaToDelete(mfa);
    setOpenDeleteModal(true);
  };

  const handleConfirmDeletion = () => {
    setOpenDeleteModal(false);
    setOpenPasswordModal(true);
  };

  const handlePasswordConfirm = (password) => {
    setOpenPasswordModal(false);
    if (mfaToDelete?.id) {
      handleDeleteMfa(mfaToDelete.id, password);
    }
  };

  const handleReturnToDash = () => {
    const url = determineDashRoot();
    window.location.replace(url);
  };

  if (isLoading || isMfaLoading) {
    return <FlexColumn
      sx={{
        width: '100vw',
        height: 'calc(100vh - 50px)',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <LocalIQSpinner />
    </FlexColumn>;
  }

  return (
    <>
      {isAuthenticated ? (
      <>
        {deleteError && (
        <StyledAlert
          severity="error"
          onClose={clearDeleteError}
          sx={{ mb: 3 }}
          iconMapping={{ error: <WarningAmberOutlinedIcon sx={{ width: '32px', height: '32px' }}/> }}
        >
          <Typography variant="body1" color={"error.main"} fontWeight={'600'}>Removal Failed</Typography>
          <Typography variant="body2">{deleteError}</Typography>
        </StyledAlert>
        )}
      <FlexColumn sx={{ gap: (theme) =>  theme.spacing(3) }}>
        <FlexColumn sx={{ gap: (theme) =>  theme.spacing(1) }}>
          <Typography variant='h4'>Auth0 Settings</Typography>
          <BodyLarge variant='body1'>Manage your enrolled authenticators.</BodyLarge>
        </FlexColumn>
        <StyledCard elevation={0} variant="outlined">
          <CardHeader
            sx={{ p: 0, m: 0 }}
            title="MFA Settings"
            titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
          />
          <CardContent sx={{ px: 0, py: 3 }}>
              {
                Array.isArray(processedMfaList)
                && processedMfaList.length > 0 ? (
                    <FlexRowWithFlexWrap>
                      {
                        processedMfaList.map((mfa) => (
                          <StyledCard
                            elevation={0}
                            key={mfa.id}
                            variant="outlined"
                            sx={{ flex: { sm: '1 0 0' } }}
                          >
                            <Tile>
                              <IconWrapper>
                                <>{mfa.icon}</>
                                <CircleRoundedIcon sx={{ fontSize: '12px' }} />
                              </IconWrapper>
                              <FlexColumn sx={{ alignItems: 'start' }}>
                                <Typography variant="body1" fontWeight="bold">
                                  {mfa.name}
                                </Typography>
                                <Typography variant="body2">
                                  Enrolled using Phone
                                </Typography>
                                <RemoveButton
                                  variant="text"
                                  color="error"
                                  type="button"
                                  onClick={() => handleOpenDeleteModal(mfa)}
                                >
                                  Remove
                                </RemoveButton>
                              </FlexColumn>
                            </Tile>
                          </StyledCard>
                        ))
                      }
                    </FlexRowWithFlexWrap>
                ) : (
                  <BodyLarge sx={{ py: 5 }}>
                    No Authenticators are currently configured.
                    {' '}
                    <b>Select “Add Authenticator” to continue.</b>
                  </BodyLarge>
                )
              }
          </CardContent>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleAddMfa}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
            disabled={isEnrolling}
            startIcon={<Add />}
          >
              Add Authenticator
          </Button>
        </StyledCard>
        <Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleReturnToDash}
          >
            Return to Dash
          </Button>
        </Box>
      </FlexColumn>
      </>
    ) : (
      <Typography variant='h4'
        sx={{ textAlign: 'center', color: (theme) => theme.palette.error.main }}
      >
         Not logged in
      </Typography>
      )}
      <DeleteMfaModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onConfirm={handleConfirmDeletion}
        mfaName={mfaToDelete?.name}
      />
      <PasswordConfirmationModal
        open={openPasswordModal}
        onClose={() => setOpenPasswordModal(false)}
        onConfirm={handlePasswordConfirm}
      />
    </>
  );
}
